import { Injectable, inject } from '@angular/core';
import { DictionaryManagerService } from './dictionary-manager.service';
import { SpellCheckCommand } from '../helpers/enums/spellcheck-command';
import { SpellCheckRequest } from '../interfaces/spellcheck-request';
import { Subject, Subscription } from 'rxjs';
import { SpellCheckMessage } from '../interfaces/spellcheck-message';

@Injectable()
export class SpellcheckWorkerAdapterService {
  private readonly dictionaryManager = inject(DictionaryManagerService);

  private worker?: Worker;
  private subscription?: Subscription;

  message$ = new Subject<SpellCheckMessage>();
  ready$ = new Subject<void>();

  hasWorker(): boolean {
    return this.worker !== undefined;
  }

  build(): void {
    if (!this.worker) {
      this.worker = this.newWorker();
      this.subscription = this.dictionaryManager.dictionary.subscribe(
        (word: string) => {
          this.post({
            command: SpellCheckCommand.AddWord,
            word,
          });
        }
      );
    }
  }

  post(request: SpellCheckRequest): void {
    if (request.command === SpellCheckCommand.AddWord) {
      this.dictionaryManager.addWord(request.word);
    }
    if (this.hasWorker()) {
      this.worker!.postMessage(request);
    }
  }

  destroy(): void {
    if (this.subscription) {
      this.subscription!.unsubscribe();
      this.subscription = undefined;
    }

    if (this.worker) {
      this.worker!.terminate();
      this.worker = undefined;
    }
  }

  private newWorker(): Worker | undefined {
    const worker = new Worker(
      new URL('./spellchecker.worker', import.meta.url),
      {
        type: 'module',
      }
    );

    worker.onmessage = this.messageHandler.bind(this);
    worker.onerror = this.errorHandler.bind(this);
    worker.onmessageerror = this.errorMessageHandler.bind(this);

    return worker;
  }

  private messageHandler(e: MessageEvent<SpellCheckMessage>): void {
    if (e.data.error) {
      console.error('Error reported from worker:', e.data.error);
    } else if (e.data.type === 'ready') {
      this.ready$.next();
    } else {
      this.message$.next(e.data);
    }
  }

  private errorHandler(event: ErrorEvent): void {
    console.error(
      'Error on the Worker:',
      event.message,
      'in',
      event.filename,
      'line:',
      event.lineno
    );
  }

  private errorMessageHandler(event: MessageEvent<unknown>): void {
    console.error('Error processing the message sent to the worker:', event);
  }
}
